import type { DocumentType } from '@aws-amplify/core/internals/utils';
import { post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useCallback } from 'react';
import type { AcctStateRefResponseType } from 'types/api/acctStateRefResponseType';
import type { CompanyImageSearchItemType } from 'types/api/companyImageSearchItemType';
import type { CompanyMemberSearchItemType } from 'types/api/companyMemberSearchItemType';
import type { ContentSearchResponseType } from 'types/api/contentSearchResponseType';
import type { DiagnosisReportRefResponseType } from 'types/api/diagnosisReportRefResponseType';
import type { DiagnosisRespondentRefResponseType } from 'types/api/diagnosisRespondentRefResponseType';
import type { DiagnosisResponseType } from 'types/api/diagnosisResponseType';
import type { FormReportRefResponseType } from 'types/api/formReportRefResponseType';
import type { FormRespondentRefResponseType } from 'types/api/formRespondentRefResponseType';
import type { FormResponseType } from 'types/api/formResponseType';
import type { InvitationRefResponseType } from 'types/api/invitationRefResponseType';
import type { MasterRefResponseType } from 'types/api/masterRefResponseType';
import type { QuizReportRefResponseType } from 'types/api/quizReportRefResponseType';
import type { QuizRespondentRefResponseType } from 'types/api/quizRespondentRefResponseType';
import type { QuizResponseType } from 'types/api/quizResponseType';
import type { ReportSearchItemType } from 'types/api/reportSearchItemType';
import type { RespondentSearchItemType } from 'types/api/respondentSearchItemType';
import type { VoiceChatReportRefResponseType } from 'types/api/voiceChatReportRefResponseType';
import type { VoiceChatRespondentRefResponseType } from 'types/api/voiceChatRespondentRefResponseType';
import type { VoiceChatResponseType } from 'types/api/voiceChatResponseType';
import type { VoiceChatViewerRefResponseType } from 'types/api/voiceChatViewerRefResponseType';

/**
 * API連携カスタムフック
 */
export const useApiState = () => {
  // const navigate = useNavigate();

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * POST送信
   */
  const _post = async (
    path: string,
    requestBody: DocumentType
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> => {
    const myInit = {
      body: requestBody,
      headers:
        import.meta.env.VITE_STAGE === 'dev'
          ? {
              'cognito-identity-id': (await fetchAuthSession()).identityId,
            }
          : {},
    };

    try {
      const response = await post({
        apiName: 'admin-api',
        path: path,
        options: myInit,
      }).response;
      return await response.body.json();
    } catch (e) {
      console.debug('API通信エラー', e);
      throw e;
    }
  };

  return {
    // アカウント存在検証API
    postAcctVerify: useCallback(
      async (request: {
        mailAddress: string;
      }): Promise<{
        valid: boolean;
      }> => {
        return await _post('/acct_verify', request);
      },
      []
    ),

    // アカウント状態参照API
    postAcctStateRef: useCallback(
      async (request: {
        target: ('user' | 'company' | 'usage' | 'activeContent')[];
        companyCd: string;
        proxyLogin: boolean;
      }): Promise<AcctStateRefResponseType> => {
        return await _post('/acct_state_ref', request);
      },
      []
    ),

    // アカウント更新API
    postAcctUpdate: useCallback(
      async (request: {
        companyId: number;
        name: string;
      }): Promise<{
        error: {
          duplicated: boolean;
        } | null;
      }> => {
        return await _post('/acct_update', request);
      },
      []
    ),

    // ベンチマーク接続確認API
    postBenchmarkConnectionValid: useCallback(
      async (request: {
        apiKey: string;
        contactListId: string;
      }): Promise<{
        valid: boolean;
      }> => {
        return await _post('/benchmark_connection_valid', request);
      },
      []
    ),

    // カンパニー画像削除API
    postCompanyImageDelete: useCallback(
      async (request: { companyImageId: number }): Promise<void> => {
        return await _post('/company_image_delete', request);
      },
      []
    ),

    // カンパニー画像生成API
    postCompanyImageGenerator: useCallback(
      async (request: {
        companyId: number;
        sharedPrompt: string | null;
        imageKey: string | null;
        imageDescriptions: {
          key: string;
          name: string;
          title: string | null;
          description: string | null;
        }[];
        saveTemporarily: boolean;
      }): Promise<{
        aiImageUsageCount: number;
        images: {
          id: number;
          name: string;
          imageKey: string;
          imageWidth: number;
          imageHeight: number;
          imageSize: number;
        }[];
      }> => {
        return await _post('/company_image_generator', request);
      },
      []
    ),

    // カンパニー画像登録API
    postCompanyImageRegister: useCallback(
      async (request: {
        companyId: number;
        name: string;
        imageWidth: number;
        imageHeight: number;
        imageSize: number;
        uploadKey: string;
      }): Promise<{
        id: number;
        imageKey: string;
      }> => {
        return await _post('/company_image_register', request);
      },
      []
    ),

    // カンパニー画像AI生成API
    postCompanyImageRegisterAi: useCallback(
      async (request: {
        companyId: number;
        prompt: string | null;
        contentTitle?: string | null;
        contentDescription?: string | null;
      }): Promise<{
        id: number;
        name: string;
        imageKey: string;
        imageWidth: number;
        imageHeight: number;
        imageSize: number;
        aiImageUsageCount: number;
      }> => {
        return await _post('/company_image_register_ai', request);
      },
      []
    ),

    // カンパニー画像検索API
    postCompanyImageSearch: useCallback(
      async (request: {
        companyId: number;
      }): Promise<{
        images: CompanyImageSearchItemType[];
      }> => {
        return await _post('/company_image_search', request);
      },
      []
    ),

    // カンパニーメンバー削除API
    postCompanyMemberDelete: useCallback(
      async (request: { companyMemberId: number }): Promise<void> => {
        return await _post('/company_member_delete', request);
      },
      []
    ),

    // カンパニーメンバー検索API
    postCompanyMemberSearch: useCallback(
      async (request: {
        companyId: number;
      }): Promise<{
        accounts: CompanyMemberSearchItemType[];
      }> => {
        return await _post('/company_member_search', request);
      },
      []
    ),

    // カンパニーメンバー更新API
    postCompanyMemberUpdate: useCallback(
      async (request: {
        companyMemberId: number;
        roleDiv: number;
        userName: string;
      }): Promise<void> => {
        return await _post('/company_member_update', request);
      },
      []
    ),

    // カンパニー登録API
    postCompanyRegister: useCallback(
      async (request: {
        company: {
          name: string;
          tel: string;
          zipCode: string;
          prefectureDiv: number;
          city: string;
          address: string;
          buildingName: string | null;
          imageUploadKey: string | null;
          reportMailSenderName: string | null;
          reportMailReplyAddress: string | null;
          reportMailSignatureHtml: string | null;
          privacyPolicyUrl: string | null;
          copyright: string | null;
          termsInputDiv: number;
          termsUrl: string | null;
          termsText: string | null;
          apiKeyBenchmark: string | null;
          apiKeyGoogleAnalytics: string | null;
        };
        user: {
          accessToken: string;
          name: string;
          loginRouteDiv: number;
        } | null;
      }): Promise<{
        companyCd: string | null;
        error: {
          duplicated: boolean;
        } | null;
      }> => {
        return await _post('/company_register', request);
      },
      []
    ),

    // カンパニー更新API
    postCompanyUpdate: useCallback(
      async (request: {
        companyId?: number;
        name: string;
        tel: string;
        zipCode: string;
        prefectureDiv: number;
        city: string;
        address: string;
        buildingName: string | null;
        imageUploadKey: string | null;
        imageResetFlag: boolean;
        reportMailSenderName: string | null;
        reportMailReplyAddress: string | null;
        reportMailSignatureHtml: string | null;
        privacyPolicyUrl: string | null;
        copyright: string | null;
        termsInputDiv: number;
        termsUrl: string | null;
        termsText: string | null;
        apiKeyBenchmark: string | null;
        apiKeyGoogleAnalytics: string | null;
      }): Promise<{
        error: {
          duplicated: boolean;
        } | null;
      }> => {
        return await _post('/company_update', request);
      },
      []
    ),

    // コンテンツ検索API
    postContentSearch: useCallback(
      async (request: {
        companyId: number;
        limit: number;
        offset: number;
      }): Promise<ContentSearchResponseType> => {
        return await _post('/content_search', request);
      },
      []
    ),

    // 診断AI選択肢作成API
    postDiagnosisAiOptions: useCallback(
      async (request: {
        companyId: number;
        title: string;
        description: string | null;
        questionTitle: string;
        resultTitles: string[];
      }): Promise<{
        aiTextUsageCount: number;
        options: {
          title: string | null;
          diagnosisResultList: string | null;
        }[];
        referenceUrl: string | null;
      }> => {
        return await _post('/diagnosis_ai_options', request);
      },
      []
    ),

    // 診断AI質問作成API
    postDiagnosisAiQuestions: useCallback(
      async (request: {
        companyId: number;
        prompt: string;
        currentContent: {
          title: string | null;
          description: string | null;
          questions: {
            name: string;
            title: string | null;
            options: {
              title: string | null;
              diagnosisResultList: string | null;
            }[];
          }[];
          results: {
            title: string | null;
            descriptionHtml: string | null;
          }[];
        };
        history: { role: string; text: string }[];
      }): Promise<{
        aiTextUsageCount: number;
        answerText: string;
        answerContent: {
          title: string | null;
          description: string | null;
          questions: {
            title: string | null;
            options: {
              title: string | null;
              diagnosisResultList: string | null;
            }[];
          }[];
          results: {
            title: string;
            descriptionHtml: string;
          }[];
        } | null;
        answerPatch: {
          title: string | null;
          description: string | null;
          questions:
            | {
                index: number;
                question: {
                  title: string | null;
                  options: {
                    title: string | null;
                    diagnosisResultList: string | null;
                  }[];
                };
              }[]
            | null;
          results:
            | {
                index: number;
                result: {
                  title: string;
                  descriptionHtml: string;
                };
              }[]
            | null;
        } | null;
        suggestions: { title: string; description: string }[] | null;
      }> => {
        return await _post('/diagnosis_ai_questions', request);
      },
      []
    ),

    // 診断AI結果説明作成API
    postDiagnosisAiResults: useCallback(
      async (request: {
        companyId: number;
        title: string;
        description: string | null;
        resultTitle: string;
      }): Promise<{
        aiTextUsageCount: number;
        descriptionHtml: string;
        referenceUrl: string | null;
      }> => {
        return await _post('/diagnosis_ai_results', request);
      },
      []
    ),

    // 診断複製API
    postDiagnosisCopy: useCallback(async (request: { diagnosisId: number }): Promise<void> => {
      return await _post('/diagnosis_copy', request);
    }, []),

    // 診断削除API
    postDiagnosisDelete: useCallback(async (request: { diagnosisId: number }): Promise<void> => {
      return await _post('/diagnosis_delete', request);
    }, []),

    // 診断参照API
    postDiagnosisRef: useCallback(
      async (request: { diagnosisCd: string }): Promise<DiagnosisResponseType> => {
        return await _post('/diagnosis_ref', request);
      },
      []
    ),

    // 診断公開API
    postDiagnosisPublish: useCallback(
      async (request: {
        diagnosisId: number;
        mode: 'preview' | 'production';
      }): Promise<DiagnosisResponseType> => {
        return await _post('/diagnosis_publish', request);
      },
      []
    ),

    // 診断公開停止API
    postDiagnosisPublishTerminate: useCallback(
      async (request: {
        diagnosisId: number;
        mode: 'preview' | 'production';
      }): Promise<DiagnosisResponseType> => {
        return await _post('/diagnosis_publish_terminate', request);
      },
      []
    ),

    // 診断登録API
    postDiagnosisRegister: useCallback(
      async (request: {
        companyId: number;
        diagnosisStatusDiv: number;
        title: string | null;
        titleHiddenFlag: boolean;
        description: string | null;
        respondentMailDiv: number;
        imageKey: string;
        reportMailSubject: string | null;
        reportMailSenderName: string | null;
        reportMailSignatureHtml: string | null;
        diagnosisTemplateId: number | null;
        publishStartAt: number | null;
        publishEndAt: number | null;
        noIndexFlag: boolean;
        benchmarkContactListId: string | null;
        gtmId: string | null;
        respondentFields: {
          getFlag: boolean;
          respondentFieldDiv: number | null;
          respondentStandardFieldDiv: number | null;
          label: string | null;
          placeholder: string | null;
          respondentCustomFieldDiv: number | null;
          respondentCustomFieldInputDiv: number | null;
          maxLength: number | null;
        }[];
        lineFlag: boolean;
        lineUrl: string | null;
        questions: {
          title: string | null;
          questionImageKey: string | null;
          options: {
            title: string | null;
          }[];
        }[];
        results: {
          title: string;
          descriptionHtml: string;
          imageKey: string;
        }[];
      }): Promise<DiagnosisResponseType> => {
        return await _post('/diagnosis_register', request);
      },
      []
    ),

    // 診断レポートダウンロードAPI
    postDiagnosisReportDownload: useCallback(
      async (request: {
        diagnosisCd: string;
        reportDateFrom: string;
        reportDateTo: string;
        encoding?: 'utf8' | 'shift_jis';
      }): Promise<{
        fileName: string;
        fileData: string;
        fileDataCount: number;
      }> => {
        return await _post('/diagnosis_report_download', request);
      },
      []
    ),

    // 診断レポート参照API
    postDiagnosisReportRef: useCallback(
      async (request: {
        diagnosisCd: string;
        reportDateFrom: string;
        reportDateTo: string;
      }): Promise<DiagnosisReportRefResponseType> => {
        return await _post('/diagnosis_report_ref', request);
      },
      []
    ),

    // 診断獲得リストダウンロードAPI
    postDiagnosisRespondentDownload: useCallback(
      async (request: {
        diagnosisCd: string;
        reportDateFrom: string;
        reportDateTo: string;
        encoding?: 'utf8' | 'shift_jis';
      }): Promise<{
        fileName: string;
        fileData: string;
        fileDataCount: number;
      }> => {
        return await _post('/diagnosis_respondent_download', request);
      },
      []
    ),

    // 診断獲得リスト参照API
    postDiagnosisRespondentRef: useCallback(
      async (request: {
        diagnosisCd: string;
        limit: number;
        offset: number;
      }): Promise<DiagnosisRespondentRefResponseType> => {
        return await _post('/diagnosis_respondent_ref', request);
      },
      []
    ),

    // 診断更新API
    postDiagnosisUpdate: useCallback(
      async (request: {
        diagnosisId: number;
        diagnosisStatusDiv: number;
        title: string | null;
        titleHiddenFlag: boolean;
        description: string | null;
        respondentMailDiv: number;
        imageKey: string;
        reportMailSubject: string | null;
        reportMailSenderName: string | null;
        reportMailSignatureHtml: string | null;
        diagnosisTemplateId: number | null;
        publishStartAt: number | null;
        publishEndAt: number | null;
        noIndexFlag: boolean;
        benchmarkContactListId: string | null;
        gtmId: string | null;
        respondentFieldGroupId: number | null;
        respondentFields: {
          getFlag: boolean;
          respondentFieldDiv: number | null;
          respondentStandardFieldDiv: number | null;
          label: string | null;
          placeholder: string | null;
          respondentCustomFieldDiv: number | null;
          respondentCustomFieldInputDiv: number | null;
          maxLength: number | null;
        }[];
        lineFlag: boolean;
        lineUrl: string | null;
        questions: {
          title: string | null;
          questionImageKey: string | null;
          options: {
            title: string | null;
          }[];
        }[];
        results: {
          title: string;
          descriptionHtml: string;
          imageKey: string;
        }[];
      }): Promise<DiagnosisResponseType> => {
        return await _post('/diagnosis_update', request);
      },
      []
    ),

    // 招待受付API
    postInvitationAccept: useCallback(
      async (request: {
        invitationCd: string;
        user: {
          accessToken: string;
          name: string;
          loginRouteDiv: number;
        } | null;
      }): Promise<{
        companyCd: string;
      }> => {
        return await _post('/invitation_accept', request);
      },
      []
    ),

    // 招待削除API
    postInvitationDelete: useCallback(
      async (request: { companyMemberId: number }): Promise<void> => {
        return await _post('/invitation_delete', request);
      },
      []
    ),

    // 招待参照API
    postInvitationRef: useCallback(
      async (request: { invitationCd: string }): Promise<InvitationRefResponseType> => {
        return await _post('/invitation_ref', request);
      },
      []
    ),

    // 招待再送信API
    postInvitationResend: useCallback(
      async (request: { companyMemberId: number }): Promise<void> => {
        return await _post('/invitation_resend', request);
      },
      []
    ),

    // 招待送信API
    postInvitationSend: useCallback(
      async (request: {
        companyId: number;
        mailAddress: string;
        roleDiv: number;
      }): Promise<{
        member: {
          id: number;
          roleDiv: number;
          companyMemberStatusDiv: number;
          invitationMailAddress: string | null;
          invitedAt: number | null;
          joinedAt: number | null;
        };
      }> => {
        return await _post('/invitation_send', request);
      },
      []
    ),

    // マスター情報参照API
    postMasterRef: useCallback(async (): Promise<MasterRefResponseType> => {
      return await _post('/master_ref', {});
    }, []),

    // 代理ログイン時の処理
    postProxyLogin: useCallback(
      async (
        request: Record<string, never>
      ): Promise<{
        valid: boolean;
        companyCd: string;
      }> => {
        return await _post('/proxy_login', request);
      },
      []
    ),

    // 代理ログアウト時の処理
    postProxyLogout: useCallback(async (request: Record<string, never>): Promise<void> => {
      return await _post('/proxy_logout', request);
    }, []),

    // クイズ複製API
    postQuizCopy: useCallback(async (request: { quizId: number }): Promise<void> => {
      return await _post('/quiz_copy', request);
    }, []),

    // クイズ削除API
    postQuizDelete: useCallback(async (request: { quizId: number }): Promise<void> => {
      return await _post('/quiz_delete', request);
    }, []),

    // クイズ公開API
    postQuizPublish: useCallback(
      async (request: {
        quizId: number;
        mode: 'preview' | 'production';
      }): Promise<QuizResponseType> => {
        return await _post('/quiz_publish', request);
      },
      []
    ),

    // クイズ公開停止API
    postQuizPublishTerminate: useCallback(
      async (request: {
        quizId: number;
        mode: 'preview' | 'production';
      }): Promise<QuizResponseType> => {
        return await _post('/quiz_publish_terminate', request);
      },
      []
    ),

    // クイズ参照API
    postQuizRef: useCallback(async (request: { quizCd: string }): Promise<QuizResponseType> => {
      return await _post('/quiz_ref', request);
    }, []),

    // クイズ登録API
    postQuizRegister: useCallback(
      async (request: {
        companyId: number;
        quizStatusDiv: number;
        title: string | null;
        titleHiddenFlag: boolean;
        description: string | null;
        respondentMailDiv: number;
        imageKey: string;
        reportMailSubject: string | null;
        reportMailSenderName: string | null;
        reportMailSignatureHtml: string | null;
        quizTemplateId: number | null;
        publishStartAt: number | null;
        publishEndAt: number | null;
        noIndexFlag: boolean;
        benchmarkContactListId: string | null;
        gtmId: string | null;
        respondentFields: {
          getFlag: boolean;
          respondentFieldDiv: number | null;
          respondentStandardFieldDiv: number | null;
          label: string | null;
          placeholder: string | null;
          respondentCustomFieldDiv: number | null;
          respondentCustomFieldInputDiv: number | null;
          maxLength: number | null;
        }[];
        lineFlag: boolean;
        lineUrl: string | null;
        questions: {
          title: string | null;
          descriptionHtml: string | null;
          reportMailDescriptionHtml: string | null;
          questionImageKey: string | null;
          answerImageKey: string | null;
          options: {
            title: string | null;
            correctFlag: boolean;
          }[];
        }[];
        results: {
          scoreThreshold: number;
          title: string;
          descriptionHtml: string;
          imageKey: string;
        }[];
      }): Promise<QuizResponseType> => {
        return await _post('/quiz_register', request);
      },
      []
    ),

    // クイズレポートダウンロードAPI
    postQuizReportDownload: useCallback(
      async (request: {
        quizCd: string;
        reportDateFrom: string;
        reportDateTo: string;
        encoding?: 'utf8' | 'shift_jis';
      }): Promise<{
        fileName: string;
        fileData: string;
        fileDataCount: number;
      }> => {
        return await _post('/quiz_report_download', request);
      },
      []
    ),

    // クイズレポート参照API
    postQuizReportRef: useCallback(
      async (request: {
        quizCd: string;
        reportDateFrom: string;
        reportDateTo: string;
      }): Promise<QuizReportRefResponseType> => {
        return await _post('/quiz_report_ref', request);
      },
      []
    ),

    // クイズ獲得リストダウンロードAPI
    postQuizRespondentDownload: useCallback(
      async (request: {
        quizCd: string;
        reportDateFrom: string;
        reportDateTo: string;
        encoding?: 'utf8' | 'shift_jis';
      }): Promise<{
        fileName: string;
        fileData: string;
        fileDataCount: number;
      }> => {
        return await _post('/quiz_respondent_download', request);
      },
      []
    ),

    // クイズ獲得リスト参照API
    postQuizRespondentRef: useCallback(
      async (request: {
        quizCd: string;
        limit: number;
        offset: number;
      }): Promise<QuizRespondentRefResponseType> => {
        return await _post('/quiz_respondent_ref', request);
      },
      []
    ),

    // クイズ更新API
    postQuizUpdate: useCallback(
      async (request: {
        quizId: number;
        quizStatusDiv: number;
        title: string | null;
        titleHiddenFlag: boolean;
        description: string | null;
        respondentMailDiv: number;
        imageKey: string;
        reportMailSubject: string | null;
        reportMailSenderName: string | null;
        reportMailSignatureHtml: string | null;
        quizTemplateId: number | null;
        publishStartAt: number | null;
        publishEndAt: number | null;
        noIndexFlag: boolean;
        benchmarkContactListId: string;
        gtmId: string | null;
        respondentFieldGroupId: number | null;
        respondentFields: {
          getFlag: boolean;
          respondentFieldDiv: number | null;
          respondentStandardFieldDiv: number | null;
          label: string | null;
          placeholder: string | null;
          respondentCustomFieldDiv: number | null;
          respondentCustomFieldInputDiv: number | null;
          maxLength: number | null;
        }[];
        lineFlag: boolean;
        lineUrl: string | null;
        questions: {
          title: string | null;
          descriptionHtml: string | null;
          reportMailDescriptionHtml: string | null;
          questionImageKey: string | null;
          answerImageKey: string | null;
          options: {
            title: string | null;
            correctFlag: boolean;
          }[];
        }[];
        results: {
          scoreThreshold: number;
          title: string;
          descriptionHtml: string;
          imageKey: string;
        }[];
      }): Promise<QuizResponseType> => {
        return await _post('/quiz_update', request);
      },
      []
    ),

    // レポート検索API
    postReportSearch: useCallback(
      async (request: {
        companyId: number;
        limit: number;
        offset: number;
      }): Promise<{
        contents: ReportSearchItemType[];
        hasNext: boolean;
      }> => {
        return await _post('/report_search', request);
      },
      []
    ),

    // レポート検索API
    postRespondentSearch: useCallback(
      async (request: {
        companyId: number;
        limit: number;
        offset: number;
      }): Promise<{
        contents: RespondentSearchItemType[];
        hasNext: boolean;
      }> => {
        return await _post('/respondent_search', request);
      },
      []
    ),

    // クイズAI選択肢作成API
    postQuizAiOptions: useCallback(
      async (request: {
        companyId: number;
        title: string;
        description: string | null;
        questionTitle: string;
      }): Promise<{
        aiTextUsageCount: number;
        descriptionHtml: string | null;
        options: {
          title: string | null;
          correctFlag: boolean;
        }[];
        referenceUrl: string | null;
      }> => {
        return await _post('/quiz_ai_options', request);
      },
      []
    ),

    // クイズAI質問作成API
    postQuizAiQuestions: useCallback(
      async (request: {
        companyId: number;
        prompt: string;
        currentContent: {
          title: string | null;
          description: string | null;
          questions: {
            name: string;
            title: string | null;
            descriptionHtml: string | null;
            options: {
              title: string | null;
              correctFlag: boolean | null;
            }[];
          }[];
          results: {
            scoreThreshold: number | null;
            title: string | null;
            descriptionHtml: string | null;
          }[];
        };
        history: { role: string; text: string }[];
      }): Promise<{
        aiTextUsageCount: number;
        answerText: string;
        answerContent: {
          title: string | null;
          description: string | null;
          questions: {
            title: string | null;
            descriptionHtml: string | null;
            options: {
              title: string | null;
              correctFlag: boolean;
            }[];
          }[];
          results: {
            scoreThreshold: number;
            title: string;
            descriptionHtml: string;
          }[];
        } | null;
        answerPatch: {
          title: string | null;
          description: string | null;
          questions:
            | {
                index: number;
                question: {
                  title: string | null;
                  descriptionHtml: string | null;
                  options: {
                    title: string | null;
                    correctFlag: boolean;
                  }[];
                };
              }[]
            | null;
          results:
            | {
                index: number;
                result: {
                  scoreThreshold: number;
                  title: string;
                  descriptionHtml: string;
                };
              }[]
            | null;
        } | null;
        suggestions: { title: string; description: string }[] | null;
      }> => {
        return await _post('/quiz_ai_questions', request);
      },
      []
    ),

    // フォームAI質問作成API
    postFormAiQuestions: useCallback(
      async (request: {
        companyId: number;
        prompt: string;
        currentContent: {
          title: string | null;
          description: string | null;
          questions: {
            name: string;
            title: string | null;
            questionImageKey: string | null;
            questionType: number | null;
            placeholder: string | null;
            requiredFlag: boolean | null;
            textMaxLength: number | null;
            numberMaxLength: number | null;
            checkboxMinCount: number | null;
            checkboxMaxCount: number | null;
            options: {
              title: string | null;
              imageKey: string | null;
            }[];
          }[];
          results: {
            title: string | null;
            descriptionHtml: string | null;
            imageKey: string | null;
          }[];
        };
        history: { role: string; text: string }[];
      }): Promise<{
        aiTextUsageCount: number;
        answerText: string;
        answerContent: {
          title: string | null;
          description: string | null;
          questions: {
            title: string | null;
            questionImageKey: string | null;
            questionType: number | null;
            placeholder: string | null;
            requiredFlag: boolean | null;
            textMaxLength: number | null;
            numberMaxLength: number | null;
            checkboxMinCount: number | null;
            checkboxMaxCount: number | null;
            options: {
              title: string | null;
              imageKey: string | null;
            }[];
          }[];
          results: {
            title: string;
            descriptionHtml: string;
            imageKey: string | null;
          }[];
        } | null;
        answerPatch: {
          title: string | null;
          description: string | null;
          questions:
            | {
                index: number;
                question: {
                  title: string | null;
                  questionImageKey: string | null;
                  questionType: number | null;
                  placeholder: string | null;
                  requiredFlag: boolean | null;
                  textMaxLength: number | null;
                  numberMaxLength: number | null;
                  checkboxMinCount: number | null;
                  checkboxMaxCount: number | null;
                  options: {
                    title: string | null;
                    imageKey: string | null;
                  }[];
                };
              }[]
            | null;
          results:
            | {
                index: number;
                result: {
                  title: string;
                  descriptionHtml: string;
                  imageKey: string | null;
                };
              }[]
            | null;
        } | null;
        suggestions: { title: string; description: string }[] | null;
      }> => {
        return await _post('/form_ai_questions', request);
      },
      []
    ),

    // フォーム複製API
    postFormCopy: useCallback(async (request: { formId: number }): Promise<void> => {
      return await _post('/form_copy', request);
    }, []),

    // フォーム削除API
    postFormDelete: useCallback(async (request: { formId: number }): Promise<void> => {
      return await _post('/form_delete', request);
    }, []),

    // フォーム参照API
    postFormRef: useCallback(async (request: { formCd: string }): Promise<FormResponseType> => {
      return await _post('/form_ref', request);
    }, []),

    // フォーム公開API
    postFormPublish: useCallback(
      async (request: {
        formId: number;
        mode: 'preview' | 'production';
      }): Promise<FormResponseType> => {
        return await _post('/form_publish', request);
      },
      []
    ),

    // フォーム公開停止API
    postFormPublishTerminate: useCallback(
      async (request: {
        formId: number;
        mode: 'preview' | 'production';
      }): Promise<FormResponseType> => {
        return await _post('/form_publish_terminate', request);
      },
      []
    ),

    // フォーム登録API
    postFormRegister: useCallback(
      async (request: {
        companyId: number;
        formStatusDiv: number;
        title: string | null;
        titleHiddenFlag: boolean;
        description: string | null;
        respondentMailDiv: number;
        imageKey: string | null;
        reportMailFlag: boolean;
        reportMailSubject: string | null;
        reportMailSenderName: string | null;
        reportMailSignatureHtml: string | null;
        reportMailDescribeHtml: string | null;
        formTemplateId: number | null;
        publishStartAt: number | null;
        publishEndAt: number | null;
        noIndexFlag: boolean;
        benchmarkContactListId: string | null;
        gtmId: string | null;
        respondentFields: {
          getFlag: boolean;
          respondentFieldDiv: number | null;
          respondentStandardFieldDiv: number | null;
          label: string | null;
          placeholder: string | null;
          respondentCustomFieldDiv: number | null;
          respondentCustomFieldInputDiv: number | null;
          maxLength: number | null;
        }[];
        lineFlag: boolean;
        lineUrl: string | null;
        questions: {
          title: string | null;
          questionImageKey: string | null;
          questionType: number;
          minCount: number | null;
          maxCount: number | null;
          placeholder: string | null;
          requiredFlag: boolean;
          options: {
            title: string | null;
            imageKey: string | null;
          }[];
        }[];
        results: {
          title: string | null;
          descriptionHtml: string | null;
          imageKey: string | null;
        }[];
      }): Promise<FormResponseType> => {
        return await _post('/form_register', request);
      },
      []
    ),

    // フォームレポートダウンロードAPI
    postFormReportDownload: useCallback(
      async (request: {
        formCd: string;
        reportDateFrom: string;
        reportDateTo: string;
        encoding?: 'utf8' | 'shift_jis';
      }): Promise<{
        fileName: string;
        fileData: string;
        fileDataCount: number;
      }> => {
        return await _post('/form_report_download', request);
      },
      []
    ),

    // フォームレポート参照API
    postFormReportRef: useCallback(
      async (request: {
        formCd: string;
        reportDateFrom: string;
        reportDateTo: string;
      }): Promise<FormReportRefResponseType> => {
        return await _post('/form_report_ref', request);
      },
      []
    ),

    // フォーム獲得リストダウンロードAPI
    postFormRespondentDownload: useCallback(
      async (request: {
        formCd: string;
        reportDateFrom: string;
        reportDateTo: string;
        encoding?: 'utf8' | 'shift_jis';
      }): Promise<{
        fileName: string;
        fileData: string;
        fileDataCount: number;
      }> => {
        return await _post('/form_respondent_download', request);
      },
      []
    ),

    // フォーム獲得リスト参照API
    postFormRespondentRef: useCallback(
      async (request: {
        formCd: string;
        limit: number;
        offset: number;
      }): Promise<FormRespondentRefResponseType> => {
        return await _post('/form_respondent_ref', request);
      },
      []
    ),

    // フォーム更新API
    postFormUpdate: useCallback(
      async (request: {
        formId: number;
        formStatusDiv: number;
        title: string | null;
        titleHiddenFlag: boolean;
        description: string | null;
        respondentMailDiv: number;
        imageKey: string;
        reportMailFlag: boolean;
        reportMailSubject: string | null;
        reportMailSenderName: string | null;
        reportMailSignatureHtml: string | null;
        reportMailDescribeHtml: string | null;
        formTemplateId: number | null;
        publishStartAt: number | null;
        publishEndAt: number | null;
        noIndexFlag: boolean;
        benchmarkContactListId: string | null;
        gtmId: string | null;
        respondentFieldGroupId: number | null;
        respondentFields: {
          getFlag: boolean;
          respondentFieldDiv: number | null;
          respondentStandardFieldDiv: number | null;
          label: string | null;
          placeholder: string | null;
          respondentCustomFieldDiv: number | null;
          respondentCustomFieldInputDiv: number | null;
          maxLength: number | null;
        }[];
        lineFlag: boolean;
        lineUrl: string | null;
        questions: {
          title: string | null;
          questionImageKey: string | null;
          questionType: number;
          minCount: number | null;
          maxCount: number | null;
          placeholder: string | null;
          requiredFlag: boolean;
          options: {
            title: string | null;
            imageKey: string | null;
          }[];
        }[];
        results: {
          title: string;
          descriptionHtml: string;
          imageKey: string;
        }[];
      }): Promise<FormResponseType> => {
        return await _post('/form_update', request);
      },
      []
    ),

    // ボイスチャットAI質問作成API
    postVoiceChatAiQuestions: useCallback(
      async (request: {
        companyId: number;
        title: string;
        description: string | null;
        prompt: string;
        currentContent: {
          title: string | null;
          description: string | null;
          guidanceText?: string | null;
          questions: {
            title: string | null;
            questionModeDiv?: number;
            requiredFlag?: boolean;
            supplementFlag?: boolean;
            supplementPlaceholder?: string | null;
          }[];
        };
        history: {
          role: string | null;
          text: string | null;
        }[];
      }): Promise<{
        aiTextUsageCount: number;
        answerText: string;
        answerContent: {
          title: string | null;
          description: string | null;
          guidanceText?: string | null;
          questions: {
            title: string | null;
            questionModeDiv: number;
            requiredFlag: boolean;
            supplementFlag: boolean;
            supplementPlaceholder: string | null;
          }[];
          results?: {
            title: string;
            descriptionHtml: string;
            imageKey: string | null;
          }[];
        };
      }> => {
        return await _post('/voice_chat_ai_questions', request);
      },
      []
    ),

    // ボイスチャット複製API
    postVoiceChatCopy: useCallback(async (request: { voiceChatId: number }): Promise<void> => {
      return await _post('/voice_chat_copy', request);
    }, []),

    // ボイスチャット削除API
    postVoiceChatDelete: useCallback(async (request: { voiceChatId: number }): Promise<void> => {
      return await _post('/voice_chat_delete', request);
    }, []),

    // ボイスチャット参照API
    postVoiceChatRef: useCallback(
      async (request: { voiceChatCd: string }): Promise<VoiceChatResponseType> => {
        return await _post('/voice_chat_ref', request);
      },
      []
    ),

    // ボイスチャット公開API
    postVoiceChatPublish: useCallback(
      async (request: {
        voiceChatId: number;
        mode: 'preview' | 'production';
      }): Promise<VoiceChatResponseType> => {
        return await _post('/voice_chat_publish', request);
      },
      []
    ),

    // ボイスチャット公開停止API
    postVoiceChatPublishTerminate: useCallback(
      async (request: {
        voiceChatId: number;
        mode: 'preview' | 'production';
      }): Promise<VoiceChatResponseType> => {
        return await _post('/voice_chat_publish_terminate', request);
      },
      []
    ),

    // ボイスチャット登録API
    postVoiceChatRegister: useCallback(
      async (request: {
        companyId: number;
        voiceChatStatusDiv: number;
        title: string | null;
        titleHiddenFlag: boolean;
        description: string | null;
        respondentMailDiv: number | null;
        respondentFields: {
          getFlag: boolean;
          respondentFieldDiv: number | null;
          respondentStandardFieldDiv: number | null;
          label: string | null;
          placeholder: string | null;
          respondentCustomFieldDiv: number | null;
          respondentCustomFieldInputDiv: number | null;
          maxLength: number | null;
        }[];
        imageKey: string | null;
        popupImageKey?: string | null;
        designDiv: number | null;
        consentFlag: boolean;
        guidanceFlag: boolean;
        guidanceTextDisplayFlag: boolean;
        answerConfirmButtonFlag: boolean;
        answerAutoStartFlag: boolean;
        guidanceText: string | null;
        guidanceSpeechText: string | null;
        multilingualFlag: boolean;
        voiceDiv: number;
        silenceMs: number;
        maxAnswerSec: number;
        benchmarkContactListId: string | null;
        gtmId: string | null;
        trackingParamFlag: boolean;
        publishStartAt: number | null;
        publishEndAt: number | null;
        results: {
          title: string | null;
          descriptionHtml: string | null;
          imageKey: string | null;
        }[];
        questions: {
          questionModeDiv: number;
          title: string | null;
          speechText: string | null;
          requiredFlag: boolean;
          supplementFlag: boolean;
          supplementPlaceholder: string | null;
          titleDisplayFlag: boolean;
        }[];
      }): Promise<VoiceChatResponseType> => {
        return await _post('/voice_chat_register', request);
      },
      []
    ),

    // ボイスチャットレポートダウンロードAPI
    postVoiceChatReportDownload: useCallback(
      async (request: {
        voiceChatCd: string;
        reportDateFrom: string;
        reportDateTo: string;
        encoding?: 'utf8' | 'shift_jis';
      }): Promise<{
        fileName: string;
        fileData: string;
        fileDataCount: number;
      }> => {
        return await _post('/voice_chat_report_download', request);
      },
      []
    ),

    // ボイスチャットレポート参照API
    postVoiceChatReportRef: useCallback(
      async (request: {
        voiceChatCd: string;
        reportDateFrom: string;
        reportDateTo: string;
      }): Promise<VoiceChatReportRefResponseType> => {
        return await _post('/voice_chat_report_ref', request);
      },
      []
    ),

    // ボイスチャット獲得リストダウンロードAPI
    postVoiceChatRespondentDownload: useCallback(
      async (request: {
        voiceChatCd: string;
        reportDateFrom: string;
        reportDateTo: string;
        encoding?: 'utf8' | 'shift_jis';
      }): Promise<{
        fileName: string;
        fileData: string;
        fileDataCount: number;
      }> => {
        return await _post('/voice_chat_respondent_download', request);
      },
      []
    ),

    // ボイスチャット獲得リスト参照API
    postVoiceChatRespondentRef: useCallback(
      async (request: {
        voiceChatCd: string;
        limit: number;
        offset: number;
      }): Promise<VoiceChatRespondentRefResponseType> => {
        return await _post('/voice_chat_respondent_ref', request);
      },
      []
    ),

    // ボイスチャット視聴者一覧参照API
    postVoiceChatViewerRef: useCallback(
      async (request: {
        voiceChatCd: string;
        reportDateFrom: string | null;
        reportDateTo: string | null;
        viewerStatusFilterDiv: number;
        limit: number;
        offset: number;
      }): Promise<VoiceChatViewerRefResponseType> => {
        return await _post('/voice_chat_viewer_ref', request);
      },
      []
    ),

    // ボイスチャット視聴者CSVダウンロードAPI
    postVoiceChatViewerDownload: useCallback(
      async (request: {
        voiceChatCd: string;
        reportDateFrom: string | null;
        reportDateTo: string | null;
        viewerStatusFilterDiv: number;
        encoding?: 'utf8' | 'shift_jis' | null;
      }): Promise<{
        fileName: string;
        fileData: string;
        fileDataCount: number;
      }> => {
        return await _post('/voice_chat_viewer_download', request);
      },
      []
    ),

    // ボイスチャット更新API
    postVoiceChatUpdate: useCallback(
      async (request: {
        voiceChatId: number;
        companyId: number;
        voiceChatStatusDiv: number;
        title: string | null;
        titleHiddenFlag: boolean;
        description: string | null;
        respondentMailDiv: number | null;
        respondentFieldGroupId: number | null;
        respondentFields: {
          getFlag: boolean;
          respondentFieldDiv: number | null;
          respondentStandardFieldDiv: number | null;
          label: string | null;
          placeholder: string | null;
          respondentCustomFieldDiv: number | null;
          respondentCustomFieldInputDiv: number | null;
          maxLength: number | null;
        }[];
        imageKey: string | null;
        popupImageKey?: string | null;
        designDiv: number | null;
        consentFlag: boolean;
        guidanceFlag: boolean;
        guidanceTextDisplayFlag: boolean;
        answerConfirmButtonFlag: boolean;
        answerAutoStartFlag: boolean;
        guidanceText: string | null;
        guidanceSpeechText: string | null;
        multilingualFlag: boolean;
        voiceDiv: number;
        silenceMs: number;
        maxAnswerSec: number;
        benchmarkContactListId: string | null;
        gtmId: string | null;
        trackingParamFlag: boolean;
        publishStartAt: number | null;
        publishEndAt: number | null;
        results: {
          title: string | null;
          descriptionHtml: string | null;
          imageKey: string | null;
        }[];
        questions: {
          questionModeDiv: number;
          title: string | null;
          speechText: string | null;
          requiredFlag: boolean;
          supplementFlag: boolean;
          supplementPlaceholder: string | null;
          titleDisplayFlag: boolean;
        }[];
      }): Promise<VoiceChatResponseType> => {
        return await _post('/voice_chat_update', request);
      },
      []
    ),

    // 利用規約公開API
    postTermsPublish: useCallback(
      async (request: {
        companyId: number;
        termsInputDiv: number;
        termsText: string;
        copyright: string;
        mode: 'preview' | 'production';
      }): Promise<{
        success: boolean;
      }> => {
        return await _post('/terms_publish', request);
      },
      []
    ),

    // 利用規約公開停止API
    postTermsPublishTerminate: useCallback(
      async (request: {
        companyId: number;
        termsInputDiv: number;
        mode: 'preview' | 'production';
      }): Promise<{
        success: boolean;
      }> => {
        return await _post('/terms_publish_terminate', request);
      },
      []
    ),
  } as const;
};
